import { useEffect, useLayoutEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { ChevronUp, Check, Bluetooth } from 'lucide-react'
import { useVoiceStore } from '../../stores/voiceStore'
import { useLivekitStore } from '../../stores/livekitStore'
import { useDmCallStore } from '@os/messenger/web/stores/dmCallStore'
import { mediaManager, type DenoiserStatus } from '../../services/mediaManager'
import { getUserMicGainDb, setUserMicGainDb, USER_GAIN_RANGE_DB } from '../../services/sendAgc'
import DevicePreview from './DevicePreview'

// Human-readable labels + colour for the denoiser badge at the top of
// the mic picker. The state comes straight from mediaManager — it
// flips between paths depending on which WASM model actually loaded.
// The badge lets the user (and remote support) see at a glance which
// path is active without opening DevTools.
const DENOISER_BADGE: Record<DenoiserStatus, { label: string; cls: string; hint: string }> = {
  rnnoise: {
    label: 'RNNoise',
    cls: 'text-green-500',
    hint: 'RNNoise WASM worklet is active — small/fast app denoiser running.',
  },
  'agc-only': {
    label: 'AGC-only',
    cls: 'text-muted-foreground',
    hint:
      'sendAgc loudness leveling is active without a denoiser. Either the ' +
      'denoiser is still warming up (interim stopgap, swaps to RNNoise in ~1s), ' +
      'noise suppression is off, OR the cascade failed (RNNoise WASM load issue).',
  },
  browser: {
    label: 'Browser NS (fallback)',
    cls: 'text-amber-500',
    hint:
      'The selected denoiser failed to load (slow network, antivirus, or ' +
      'AudioWorklet unsupported). Falling back to Chromium native noise ' +
      'suppression — weaker, more bleed-through. Reload or check the ' +
      'DevTools console (search "Denoiser:") to debug.',
  },
  off: {
    label: 'Suppression off',
    cls: 'text-muted-foreground',
    hint: 'Noise suppression is turned off in settings. The raw mic is being published.',
  },
  idle: {
    label: 'Mic not active',
    cls: 'text-muted-foreground',
    hint: 'No microphone publication yet this session.',
  },
  'os-krisp': {
    label: 'Krisp',
    cls: 'text-indigo-400',
    hint:
      "Krisp is cleaning your mic at the OS level — we're not " +
      'layering our denoiser on top to avoid double-processing artefacts.',
  },
  'os-nvidia': {
    label: 'NVIDIA Broadcast',
    cls: 'text-lime-400',
    hint:
      "NVIDIA Broadcast is cleaning your mic at the OS level — we're not " +
      'layering our denoiser on top to avoid double-processing artefacts.',
  },
}

interface AudioDeviceInfo {
  deviceId: string
  label: string
}

// Detect Bluetooth-routed microphones from their OS-reported label.
// Windows + macOS both surface BT mics with the word "Bluetooth" or
// the HFP-profile suffix ("Hands-Free", "Headset Microphone", "BT")
// in the device label. Used to warn the user that picking a BT mic
// forces the headset out of A2DP (high-quality stereo playback) into
// HFP (narrow-band mono, both directions) — they'll suddenly hear
// peers AND be heard at noticeably lower quality. Not a bug we can
// fix in JS; it's how Bluetooth audio profiles work.
/**
 * Discord-style "Mic input volume" slider — controls the user-gain node
 * inside sendAgc (placed AFTER the AGC's makeup gain so the AGC still
 * targets the common level first, then this scales the final amount).
 *
 * Default 0 dB (unity). Range -20…+10 dB. Live-tunable via setUserMicGainDb
 * with a 50 ms ramp inside sendAgc — so dragging the slider produces no
 * zipper noise + has near-immediate audible effect.
 *
 * Use case (2026-05-26): Aleksei-class hot-mic users can self-attenuate
 * their published level instead of every listener turning down their
 * peer-volume slider individually.
 *
 * 0 dB indicator highlighted so the user can quickly return to neutral.
 */
function MicInputVolumeSlider() {
  const [db, setDb] = useState<number>(() => getUserMicGainDb())
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseFloat(e.target.value)
    setDb(v)
    setUserMicGainDb(v)
  }
  const onDoubleClick = () => {
    // Quick reset to 0 dB on double-click of the slider track.
    setDb(0)
    setUserMicGainDb(0)
  }
  const label = db === 0 ? '0 dB' : (db > 0 ? `+${db} dB` : `${db} dB`)
  return (
    <div className="px-3 py-2 border-t border-border" title="Громкость публикуемого микрофона (после sendAgc). Двойной клик — сброс на 0 dB.">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] text-muted-foreground">Mic input volume</span>
        <span className={`text-[11px] tabular-nums ${db === 0 ? 'text-muted-foreground' : db < 0 ? 'text-amber-500' : 'text-green-500'}`}>{label}</span>
      </div>
      <input
        type="range"
        min={USER_GAIN_RANGE_DB.min}
        max={USER_GAIN_RANGE_DB.max}
        step={1}
        value={db}
        onChange={onChange}
        onDoubleClick={onDoubleClick}
        className="w-full h-1 bg-muted rounded-full appearance-none cursor-pointer accent-primary"
      />
    </div>
  )
}

function isBluetoothMicLabel(label: string): boolean {
  const l = label.toLowerCase()
  return (
    l.includes('bluetooth') ||
    l.includes('hands-free') ||
    l.includes('hands free') ||
    // Common BT-only labels: "(AW725H Chat)" = Alienware HFP profile,
    // "Headset Microphone" by itself almost always means HFP on Win.
    /\bbt\b/.test(l) ||
    l.includes('headset microphone')
  )
}

export default function AudioDeviceSelector() {
  const { audioInputDeviceId, setAudioInputDevice } = useVoiceStore()
  const [devices, setDevices] = useState<AudioDeviceInfo[]>([])
  const [open, setOpen] = useState(false)
  const [denoiser, setDenoiser] = useState<DenoiserStatus>(mediaManager.getDenoiserState())
  // (D) Receive-side loudness CEILING toggle — caps peers LOUDER than the
  // ceiling (localStorage 'voiceCeilingDbfs', default -16 dBFS), downward-only
  // (echo-safe), leaving normal/quiet talkers untouched. OPT-IN, default OFF
  // (measure first — the per-peer '[loudness]' dBFS logging runs regardless of
  // this toggle). Persisted in localStorage; read live each tick by every peer's
  // ceiling loop in LiveKitBackgroundAudio, so toggling applies instantly.
  const [normalize, setNormalize] = useState(
    () => typeof window !== 'undefined' && localStorage.getItem('voiceNormalize') === 'true',
  )
  const toggleNormalize = () => {
    setNormalize((prev) => {
      const next = !prev
      try { localStorage.setItem('voiceNormalize', next ? 'true' : 'false') } catch { /* ignore */ }
      return next
    })
  }
  const containerRef = useRef<HTMLDivElement>(null)
  // OSPROD-110: the picker is rendered in a portal anchored to this
  // trigger button. menuRef lets the outside-click handler treat the
  // portalled menu (which lives outside containerRef in the DOM) as
  // "inside", and menuStyle carries the fixed-position coords.
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({})

  // Live-mirror mediaManager's denoiser state. subscribeDenoiser fires
  // immediately with the current value so we don't need to seed from
  // the getter manually — but useState's lazy initialiser above does
  // the right thing for the first paint (before useEffect lands) too.
  useEffect(() => {
    return mediaManager.subscribeDenoiser(setDenoiser)
  }, [])

  const loadDevices = async () => {
    try {
      // Request permission first so labels are available. Critically we
      // STOP the resulting tracks right away — the previous version
      // awaited the stream and discarded the reference, leaving a live
      // capture on the device. On Windows that capture stays attached
      // to the "communications" device class, so every subsequent
      // dropdown mount (this component is rendered concurrently in
      // ActiveCallBar + VoiceChannelControls + TelegramCallOverlay,
      // and each devicechange event re-runs `loadDevices`) added
      // another orphan capture. With 5+ participants the call surface
      // would have a dozen+ stray mic streams open and Chromium's
      // audio system started misbehaving — volume sliders frozen,
      // AEC reference loop muddled, system audio session flapping.
      // One-shot permission grant should never outlive enumerateDevices.
      let permissionStream: MediaStream | null = null
      try {
        permissionStream = await navigator.mediaDevices.getUserMedia({ audio: true })
      } catch {
        // Permission denied → labels stay anonymous but the picker
        // still lists deviceIds. Same UX as before.
      }
      const all = await navigator.mediaDevices.enumerateDevices()
      if (permissionStream) {
        permissionStream.getTracks().forEach((t) => {
          try { t.stop() } catch { /* noop */ }
        })
      }
      const audioInputs = all
        .filter((d) => d.kind === 'audioinput' && d.deviceId !== 'communications')
        .map((d) => ({ deviceId: d.deviceId, label: d.label || `Microphone ${d.deviceId.slice(0, 4)}` }))
      // Show a clean "Default" option instead of the raw "Default - Device Name" entry
      const filtered = audioInputs.filter((d) => d.deviceId !== 'default')
      const devicesList = [{ deviceId: 'default', label: 'Default' }, ...filtered]
      setDevices(devicesList)

      // Auto-select default if nothing selected yet
      if (!audioInputDeviceId) {
        const defaultDevice = audioInputs.find((d) => d.deviceId === 'default')
        if (defaultDevice) {
          setAudioInputDevice('default')
        } else if (audioInputs.length > 0) {
          setAudioInputDevice(audioInputs[0].deviceId)
        }
      }
    } catch {
      // Permission denied
    }
  }

  useEffect(() => {
    loadDevices()
    navigator.mediaDevices.addEventListener('devicechange', loadDevices)
    return () => navigator.mediaDevices.removeEventListener('devicechange', loadDevices)
  }, [])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      const insideTrigger = !!containerRef.current && containerRef.current.contains(target)
      const insideMenu = !!menuRef.current && menuRef.current.contains(target)
      if (!insideTrigger && !insideMenu) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('click', handler)
    return () => document.removeEventListener('click', handler)
  }, [open])

  // OSPROD-110: compute fixed-position coords for the portalled menu from
  // the trigger's viewport rect. Rendering in a portal escapes the
  // `overflow-hidden` ancestors (AppLayout / VoiceChannelView) that used
  // to clip the popup, and a high z-index keeps it above the sidebar —
  // both were why it got covered once the user zoomed the browser in.
  // Opens upward (the control bar sits at the bottom) and right-aligns to
  // the trigger, clamped to the viewport so it never runs off-screen.
  useLayoutEffect(() => {
    if (!open) return
    const compute = () => {
      const trigger = triggerRef.current
      if (!trigger) return
      const r = trigger.getBoundingClientRect()
      const margin = 8
      const menuWidth = 240 // w-60
      let right = window.innerWidth - r.right
      if (window.innerWidth - right - menuWidth < margin) {
        right = window.innerWidth - menuWidth - margin
      }
      if (right < margin) right = margin
      const bottom = window.innerHeight - r.top + margin
      setMenuStyle({ position: 'fixed', right, bottom, zIndex: 9999 })
    }
    compute()
    window.addEventListener('resize', compute)
    window.addEventListener('scroll', compute, true)
    return () => {
      window.removeEventListener('resize', compute)
      window.removeEventListener('scroll', compute, true)
    }
  }, [open])

  const handleSelect = async (deviceId: string) => {
    setAudioInputDevice(deviceId)
    setOpen(false)
    // Switch the device on whichever LiveKit rooms are active. The user
    // may be in a server voice room AND a DM call simultaneously
    // (e.g. picked up a 1:1 while standing by in #general); we want both
    // to follow the picker. Each store ignores the call when it has no
    // room of its own.
    const tasks: Promise<unknown>[] = []
    if (useVoiceStore.getState().currentChannelId) {
      tasks.push(useLivekitStore.getState().setAudioDevice(deviceId))
    }
    if (useDmCallStore.getState().room) {
      tasks.push(useDmCallStore.getState().setAudioDevice(deviceId))
    }
    try {
      await Promise.all(tasks)
    } catch (err) {
      console.error('Failed to switch audio device:', err)
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v) }}
        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-background ring-1 ring-border shadow-sm flex items-center justify-center hover:bg-accent transition z-10"
        title="Select microphone"
      >
        <ChevronUp size={12} className="text-foreground" />
      </button>

      {open && createPortal(
        <div ref={menuRef} style={menuStyle} className="bg-card rounded-lg shadow-lg ring-1 ring-border py-1 w-60">
          <div className="px-2 py-1 text-[10px] font-semibold text-muted-foreground uppercase">Microphone</div>
          {/* Denoiser status — confirms which model actually loaded.
              Useful for remote support: ask the user to open the
              picker and screenshot, instead of walking them through
              DevTools. */}
          <div
            className={`px-3 py-1 text-[10px] flex items-center gap-1 ${DENOISER_BADGE[denoiser].cls}`}
            title={DENOISER_BADGE[denoiser].hint}
          >
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-current shrink-0" />
            <span className="truncate">Denoiser: {DENOISER_BADGE[denoiser].label}</span>
          </div>
          {/* (C) Mic input volume slider — user-controlled gain applied AFTER
              sendAgc's makeup (so AGC still levels to the common target first,
              then this scales the final published amount). Default 0 dB (unity);
              lower to attenuate without forcing every listener to drop the
              peer-volume slider individually. Live-tunable via setUserMicGainDb
              (50 ms ramp, no zipper noise). Discord-style "Input volume". */}
          <MicInputVolumeSlider />
          {/* (D) Loudness ceiling toggle — caps participants louder than the
              ceiling (downward-only, echo-safe); quiet talkers are untouched
              (they're lifted at the source by send-side AGC). Read live by each
              peer's ceiling loop in LiveKitBackgroundAudio; applies immediately. */}
          <button
            onClick={toggleNormalize}
            className="w-full text-left px-3 py-1.5 text-xs text-muted-foreground hover:bg-primary/90 hover:text-white transition flex items-center gap-2 border-t border-border"
            title="Приглушает участников, которые громче порога; тихих не трогает (их подтягивает send-side). Применяется сразу."
          >
            <span
              className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${normalize ? 'bg-green-500' : 'bg-muted-foreground/40'}`}
            />
            <span className="flex-1 truncate">
              Выравнивать громкость{normalize ? ' · вкл' : ''}
            </span>
            {normalize && <Check size={12} className="text-green-500 shrink-0" />}
          </button>
          {/* The "Стабильное соединение (relay)" opt-in toggle was removed
              (2026-05-21). Forcing iceTransportPolicy:'relay' routed media
              through LiveKit's embedded TURN, but the relay path does not yet
              carry media end-to-end (clients gather no relay candidate —
              cand_relay=0 — so relay-only = inaudible). Relay must work as an
              AUTOMATIC ICE fallback, not a manual user switch; re-introduce a
              control only once TURN relay is verified carrying media. */}
          {/* The Suppress / Backend / Echo toggles previously lived
              here. They were removed once mediaManager started
              auto-routing: DFN runs by default, falls back to
              RNNoise on degradation (`onDfnDegraded`), and the
              router skips our denoiser entirely when an OS-level
              one (Krisp / NVIDIA Broadcast) is detected on the mic
              device. The badge at the top of this picker — plus
              the chip under the mic button — show the user what's
              currently active. Anything beyond that confused users
              ("which one should I pick?") and didn't change quality
              for the vast majority. Power users can still override
              via localStorage (`denoiserBackend`, `noiseSuppression`,
              `aecMode`) and a future Settings → Advanced surface. */}
          {devices.length === 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground">No devices available</div>
          )}
          {devices.map((d) => {
            const isBt = isBluetoothMicLabel(d.label)
            return (
              <button
                key={d.deviceId}
                onClick={() => handleSelect(d.deviceId)}
                className="w-full text-left px-3 py-1.5 text-xs text-muted-foreground hover:bg-primary/90 hover:text-white transition flex items-center gap-2"
                // Native title shows on hover with a small delay — good enough
                // to surface the BT tradeoff without nagging every click.
                title={
                  isBt
                    ? 'Bluetooth microphones force the headset into HFP profile: ' +
                      'both your voice AND playback drop to narrow-band mono ' +
                      '(~64 kbps). For best quality use a wired mic or your ' +
                      'laptop/camera mic with BT for output only.'
                    : undefined
                }
              >
                {audioInputDeviceId === d.deviceId && <Check size={12} className="text-green-500 shrink-0" />}
                <span className="flex-1 truncate">{d.label}</span>
                {isBt && (
                  <Bluetooth
                    size={12}
                    className="text-amber-500 shrink-0"
                    aria-label="Bluetooth microphone — degrades playback quality"
                  />
                )}
                {open && <DevicePreview deviceId={d.deviceId} />}
              </button>
            )
          })}
        </div>,
        document.body,
      )}
    </div>
  )
}
